// The controller answers a still-computing report with `processing: true` instead of rows, and the
// caller is expected to poll. v3 did this in `stores/reportStore.ts:198-200`; v4 dropped it, so a
// computing report surfaced as an error (a processing payload carries no `data` array, so the wire
// codec rejected it) and never resolved on its own.
//
// This module is the pure half: predicate + delay arithmetic, no transport and no timers, so the
// polling policy is testable without a clock. `service/**` must stay free of Vue, Pinia and
// shared/http — hence the plain error class rather than an `ApiError`.

import type { ReportResponse } from '../models/report.types'

/** v3 `DEFAULT_PROCESSING_RETRY_SEC = 30`, kept as the delay when the controller names none. */
export const DEFAULT_PROCESSING_RETRY_MS = 30_000

/**
 * v3 looped `while (true)`, so a controller stuck on `processing` span an unbounded request chain
 * for as long as the page stayed open. These two bounds are the robustness delta: a caller waits at
 * most `MAX_PROCESSING_ATTEMPTS` polls, and one hostile `retry_after_sec` cannot park a request for
 * hours.
 */
export const MAX_PROCESSING_ATTEMPTS = 10
export const MIN_PROCESSING_RETRY_MS = 1_000
export const MAX_PROCESSING_RETRY_MS = 120_000

/** Raised once the poll budget is spent, so the view can say "still computing" instead of spinning. */
export class ReportProcessingTimeoutError extends Error {
  readonly attempts: number

  constructor(attempts: number) {
    super(`Report still processing after ${attempts} attempts`)
    this.name = 'ReportProcessingTimeoutError'
    this.attempts = attempts
  }
}

/**
 * True while the controller is still computing. v3 read `data.combined || data`; here the wire codec
 * has already promoted `processing` out of a `combined` envelope, so the root is authoritative.
 */
export function isReportProcessing(response: ReportResponse | null | undefined): boolean {
  return response?.processing === true
}

/**
 * The controller's own `retry_after_sec` wins when it is a usable positive number — it knows how far
 * along the computation is. Anything else (absent, zero, negative, NaN, a string) falls back to v3's
 * 30s. The result is clamped either way: the server hints, it does not dictate.
 */
export function resolveProcessingDelayMs(response: ReportResponse | null | undefined): number {
  const retryAfterSec = Number(response?.retry_after_sec)
  const requested =
    Number.isFinite(retryAfterSec) && retryAfterSec > 0
      ? retryAfterSec * 1_000
      : DEFAULT_PROCESSING_RETRY_MS
  return Math.min(Math.max(requested, MIN_PROCESSING_RETRY_MS), MAX_PROCESSING_RETRY_MS)
}

/** Abortable sleep: settles early when the caller's signal fires, so a poll gap never pins a nav. */
export function waitForAbortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted || ms <= 0) return Promise.resolve()
  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timer)
      signal?.removeEventListener('abort', done)
      resolve()
    }
    const timer = setTimeout(done, ms)
    signal?.addEventListener('abort', done, { once: true })
  })
}
